import { MULTIPLAYER_API_BASE, MULTIPLAYER_CONFIG } from "../config/multiplayer.ts";
import type { InputIntent } from "../input/intents.ts";
import {
  MULTIPLAYER_PROTOCOL_VERSION,
  type ChatMessage,
  type CreateRoomRequest,
  type JoinRoomRequest,
  type RoomCommand,
  type RoomDirectoryEntry,
  type RoomJoinGrant,
  type ServerRoomMessage,
} from "./protocol.ts";
import type { MultiplayerRoomSnapshot } from "../sim/session/types.ts";
import { applyMultiplayerRoomDelta } from "../sim/session/delta.ts";

export type MultiplayerConnectionStatus =
  | "offline"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "error";

export interface MultiplayerClientCallbacks {
  readonly onStatus: (status: MultiplayerConnectionStatus, detail?: string) => void;
  readonly onWelcome: (value: Readonly<{
    playerId: string;
    room: RoomDirectoryEntry;
    snapshot: MultiplayerRoomSnapshot;
    chatHistory: readonly ChatMessage[];
  }>) => void;
  readonly onSnapshot: (
    snapshot: MultiplayerRoomSnapshot,
    pendingIntents: readonly InputIntent[],
  ) => void;
  readonly onChat: (message: ChatMessage) => void;
  readonly onPresence: (
    playerId: string,
    displayName: string,
    connected: boolean,
  ) => void;
  readonly onError: (message: string) => void;
}

interface StoredResumeSession {
  readonly roomId: string;
  readonly displayName: string;
  readonly appearance: JoinRoomRequest["appearance"];
  readonly resumeToken: string;
}

const RESUME_SESSION_KEY = "vilnius.multiplayer.resume-session.v2";

function readStoredResumeSession(): StoredResumeSession | null {
  try {
    const raw = window.sessionStorage.getItem(RESUME_SESSION_KEY);
    if (raw === null) return null;
    const value = JSON.parse(raw) as Partial<StoredResumeSession>;
    if (
      typeof value.roomId !== "string" ||
      typeof value.displayName !== "string" ||
      typeof value.resumeToken !== "string" ||
      value.appearance === undefined
    ) return null;
    return value as StoredResumeSession;
  } catch {
    return null;
  }
}

function writeStoredResumeSession(value: StoredResumeSession | null): void {
  try {
    if (value === null) window.sessionStorage.removeItem(RESUME_SESSION_KEY);
    else window.sessionStorage.setItem(RESUME_SESSION_KEY, JSON.stringify(value));
  } catch {
    // Private browsing/storage denial must not prevent a room connection.
  }
}

interface RejoinContext {
  readonly roomId: string;
  readonly displayName: string;
  readonly password: string;
  readonly appearance: JoinRoomRequest["appearance"];
}

interface PendingCommand {
  readonly resolve: () => void;
  readonly reject: (reason: Error) => void;
  readonly timeout: number;
}

async function jsonRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: { "content-type": "application/json", ...init?.headers },
  });
  const body = await response.json() as { error?: string } & T;
  if (!response.ok) throw new Error(body.error ?? `Request failed (${response.status})`);
  return body;
}

function websocketUrl(path: string): string {
  const url = new URL(path, window.location.href);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.href;
}

export class MultiplayerClient {
  private socket: WebSocket | null = null;
  private status: MultiplayerConnectionStatus = "offline";
  private playerId: string | null = null;
  private room: RoomDirectoryEntry | null = null;
  private resumeToken: string | null = null;
  private rejoin: RejoinContext | null = null;
  private intentionalClose = false;
  private reconnectAttempt = 0;
  private reconnectStartedAt: number | null = null;
  private reconnectTimer: number | null = null;
  private nextIntentTick = 1;
  private authoritativeSnapshot: MultiplayerRoomSnapshot | null = null;
  private lastSequence = 0;
  private inputFlushTimer: number | null = null;
  private readonly unsentIntents: InputIntent[] = [];
  private readonly pendingIntents = new Map<number, InputIntent>();
  private readonly pendingCommands = new Map<string, PendingCommand>();

  constructor(private readonly callbacks: MultiplayerClientCallbacks) {}

  get connectionStatus(): MultiplayerConnectionStatus {
    return this.status;
  }

  get connectedPlayerId(): string | null {
    return this.playerId;
  }

  get connectedRoom(): RoomDirectoryEntry | null {
    return this.room;
  }

  async listRooms(): Promise<readonly RoomDirectoryEntry[]> {
    const result = await jsonRequest<{ rooms: RoomDirectoryEntry[] }>(
      `${MULTIPLAYER_API_BASE}/rooms`,
    );
    return result.rooms;
  }

  async createRoom(request: Readonly<CreateRoomRequest>): Promise<void> {
    this.disconnect();
    this.setStatus("connecting", "Creating room…");
    const grant = await jsonRequest<RoomJoinGrant>(`${MULTIPLAYER_API_BASE}/rooms`, {
      method: "POST",
      body: JSON.stringify(request),
    });
    this.rejoin = {
      roomId: grant.room.id,
      displayName: request.displayName,
      password: request.password ?? "",
      appearance: request.appearance,
    };
    this.open(grant);
  }

  async joinRoom(
    roomId: string,
    request: Readonly<JoinRoomRequest>,
  ): Promise<void> {
    this.disconnect();
    this.setStatus("connecting", "Joining room…");
    const grant = await jsonRequest<RoomJoinGrant>(
      `${MULTIPLAYER_API_BASE}/rooms/${encodeURIComponent(roomId)}/join`,
      { method: "POST", body: JSON.stringify(request) },
    );
    this.rejoin = {
      roomId,
      displayName: request.displayName,
      password: request.password ?? "",
      appearance: request.appearance,
    };
    this.open(grant);
  }

  async resumeStoredSession(): Promise<boolean> {
    const stored = readStoredResumeSession();
    if (stored === null) return false;
    this.setStatus("connecting", "Rejoining your room…");
    try {
      const grant = await jsonRequest<RoomJoinGrant>(
        `${MULTIPLAYER_API_BASE}/rooms/${encodeURIComponent(stored.roomId)}/join`,
        {
          method: "POST",
          body: JSON.stringify({
            displayName: stored.displayName,
            appearance: stored.appearance,
            resumeToken: stored.resumeToken,
          }),
        },
      );
      this.rejoin = {
        roomId: stored.roomId,
        displayName: stored.displayName,
        password: "",
        appearance: stored.appearance,
      };
      this.resumeToken = stored.resumeToken;
      this.open(grant);
      return true;
    } catch (reason) {
      writeStoredResumeSession(null);
      this.setStatus("offline", "Your previous room is no longer available.");
      this.callbacks.onError(
        reason instanceof Error ? reason.message : "The previous room could not be rejoined.",
      );
      return false;
    }
  }

  allocateInputTick(): number {
    const tick = this.nextIntentTick;
    this.nextIntentTick += 1;
    return tick;
  }

  sendIntent(intent: Readonly<InputIntent>): void {
    if (this.status !== "connected" || this.socket?.readyState !== WebSocket.OPEN) return;
    const cloned = structuredClone(intent);
    this.pendingIntents.set(cloned.tick, cloned);
    this.unsentIntents.push(cloned);
    if (this.unsentIntents.length >= 2) {
      this.flushInputs();
    } else if (this.inputFlushTimer === null) {
      this.inputFlushTimer = window.setTimeout(() => {
        this.inputFlushTimer = null;
        this.flushInputs();
      }, 34);
    }
  }

  sendChat(text: string): void {
    this.send({ type: "chat", text });
  }

  sendCommand(command: Readonly<RoomCommand>): Promise<void> {
    if (this.status !== "connected" || this.socket?.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error("The room is not connected."));
    }
    const requestId = crypto.randomUUID();
    return new Promise<void>((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        this.pendingCommands.delete(requestId);
        reject(new Error("The room did not acknowledge that change."));
      }, 5_000);
      this.pendingCommands.set(requestId, { resolve, reject, timeout });
      this.send({ type: "command", requestId, command });
    });
  }

  disconnect(): void {
    this.closeConnection(false);
  }

  /** Close transient browser resources without discarding page-reload resume. */
  dispose(): void {
    this.closeConnection(true);
  }

  private closeConnection(preserveStoredSession: boolean): void {
    this.intentionalClose = true;
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer);
    if (this.inputFlushTimer !== null) window.clearTimeout(this.inputFlushTimer);
    this.reconnectTimer = null;
    this.inputFlushTimer = null;
    this.socket?.close(1000, "Left room");
    this.socket = null;
    this.playerId = null;
    this.room = null;
    this.resumeToken = null;
    this.rejoin = null;
    this.unsentIntents.length = 0;
    this.pendingIntents.clear();
    for (const pending of this.pendingCommands.values()) {
      window.clearTimeout(pending.timeout);
      pending.reject(new Error("The room connection closed."));
    }
    this.pendingCommands.clear();
    this.authoritativeSnapshot = null;
    this.lastSequence = 0;
    this.reconnectStartedAt = null;
    if (!preserveStoredSession) writeStoredResumeSession(null);
    this.setStatus("offline");
  }

  private open(grant: Readonly<RoomJoinGrant>): void {
    this.intentionalClose = false;
    this.room = grant.room;
    const socket = new WebSocket(websocketUrl(grant.socketPath));
    this.socket = socket;
    socket.addEventListener("open", () => {
      this.send({
        type: "hello",
        protocolVersion: MULTIPLAYER_PROTOCOL_VERSION,
        contentBuildId: MULTIPLAYER_CONFIG.contentBuildId,
        ticket: grant.ticket,
      });
    });
    socket.addEventListener("message", (event) => this.receive(event.data));
    socket.addEventListener("close", () => {
      if (this.socket === socket) this.socket = null;
      if (!this.intentionalClose) this.scheduleReconnect();
    });
    socket.addEventListener("error", () => {
      if (this.status === "connecting") this.setStatus("error", "Could not open the room connection.");
    });
  }

  private receive(data: unknown): void {
    if (typeof data !== "string") return;
    let message: ServerRoomMessage;
    try {
      message = JSON.parse(data) as ServerRoomMessage;
    } catch {
      this.callbacks.onError("The room sent an unreadable message.");
      return;
    }
    if (message.type === "welcome") {
      if (this.room === null) return;
      this.playerId = message.playerId;
      this.resumeToken = message.resumeToken;
      this.nextIntentTick = message.snapshot.game.tick + 1;
      this.authoritativeSnapshot = message.snapshot;
      this.lastSequence = message.sequence;
      this.reconnectAttempt = 0;
      this.reconnectStartedAt = null;
      this.pendingIntents.clear();
      this.setStatus("connected", this.room.name);
      if (this.rejoin !== null) {
        writeStoredResumeSession({
          roomId: this.rejoin.roomId,
          displayName: this.rejoin.displayName,
          appearance: this.rejoin.appearance,
          resumeToken: message.resumeToken,
        });
      }
      this.callbacks.onWelcome({
        playerId: message.playerId,
        room: this.room,
        snapshot: message.snapshot,
        chatHistory: message.chatHistory,
      });
      this.send({ type: "snapshot-ack", sequence: message.sequence });
      return;
    }
    if (message.type === "snapshot") {
      if (message.sequence <= this.lastSequence) return;
      if (
        message.kind === "delta" &&
        (this.authoritativeSnapshot === null || message.baseSequence !== this.lastSequence)
      ) {
        this.callbacks.onError("Room updates fell out of sequence. Rejoining from a fresh snapshot…");
        this.socket?.close(4002, "Snapshot sequence mismatch");
        return;
      }
      const snapshot = message.kind === "keyframe"
        ? message.snapshot
        : applyMultiplayerRoomDelta(this.authoritativeSnapshot!, message.delta);
      this.authoritativeSnapshot = snapshot;
      this.lastSequence = message.sequence;
      for (const tick of this.pendingIntents.keys()) {
        if (tick <= message.lastProcessedInputTick) this.pendingIntents.delete(tick);
      }
      this.nextIntentTick = Math.max(this.nextIntentTick, snapshot.game.tick + 1);
      this.callbacks.onSnapshot(snapshot, [...this.pendingIntents.values()]);
      this.send({ type: "snapshot-ack", sequence: message.sequence });
      return;
    }
    if (message.type === "chat") {
      this.callbacks.onChat(message.message);
      return;
    }
    if (message.type === "presence") {
      this.callbacks.onPresence(
        message.playerId,
        message.displayName,
        message.connected,
      );
      return;
    }
    if (message.type === "command-result") {
      const pending = this.pendingCommands.get(message.requestId);
      if (pending === undefined) return;
      window.clearTimeout(pending.timeout);
      this.pendingCommands.delete(message.requestId);
      if (message.accepted) pending.resolve();
      else pending.reject(new Error(message.reason ?? "The room rejected that change."));
      return;
    }
    if (message.type === "error") {
      this.callbacks.onError(message.message);
      if (!message.recoverable) this.disconnect();
    }
  }

  private flushInputs(): void {
    if (this.unsentIntents.length === 0) return;
    const intents = this.unsentIntents.splice(0, MULTIPLAYER_CONFIG.inputBatchMaximum);
    this.send({ type: "input", intents });
  }

  private send(message: object): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    }
  }

  private scheduleReconnect(): void {
    const context = this.rejoin;
    const resumeToken = this.resumeToken;
    if (context === null || resumeToken === null) {
      this.setStatus("error", "Connection closed before the room was ready.");
      return;
    }
    this.reconnectStartedAt ??= Date.now();
    const remainingGrace = MULTIPLAYER_CONFIG.reconnectGraceMilliseconds -
      (Date.now() - this.reconnectStartedAt);
    if (remainingGrace <= 0) {
      this.intentionalClose = true;
      this.rejoin = null;
      this.resumeToken = null;
      this.setStatus("error", "The rejoin window expired. Join the room again.");
      return;
    }
    this.setStatus("reconnecting", "Rejoining the room…");
    const delay = Math.min(remainingGrace, 4_000, 300 * 2 ** this.reconnectAttempt);
    this.reconnectAttempt += 1;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      void jsonRequest<RoomJoinGrant>(
        `${MULTIPLAYER_API_BASE}/rooms/${encodeURIComponent(context.roomId)}/join`,
        {
          method: "POST",
          body: JSON.stringify({
            displayName: context.displayName,
            password: context.password,
            appearance: context.appearance,
            resumeToken,
          }),
        },
      ).then((grant) => this.open(grant)).catch(() => this.scheduleReconnect());
    }, delay);
  }

  private setStatus(status: MultiplayerConnectionStatus, detail?: string): void {
    this.status = status;
    this.callbacks.onStatus(status, detail);
  }
}
